import { Link } from "react-router-dom";
import { ArrowUpRight, FolderKanban, Layers } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Empty, ErrorState, Loading } from "@/components/States";
import ProjectCard from "@/components/ProjectCard";
import ClientWelcome from "@/components/ClientWelcome";
import DecisionsDigest from "@/components/DecisionsDigest";
import TodayList from "@/components/TodayList";
import MyWeek from "@/components/MyWeek";
import PlatformGlance from "@/components/PlatformGlance";

const Stat = ({ label, value, testid, to }) => (
  <Link to={to} className="rounded-lg px-3 py-2 transition-colors hover:bg-secondary" data-testid={`${testid}-card`}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="stat mt-0.5 text-2xl" data-testid={testid}>{value}</p>
  </Link>
);

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };
const longDate = () => new Date().toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" });

export default function Dashboard() {
  const { user, workspace } = useAuth();
  const { data, error, loading, reload } = useFetch("/projects");
  const today = useFetch(user.role === "client" ? null : "/today");
  const admin = user.role === "admin";
  const digest = admin && !(workspace?.hidden || []).includes("client_portal");
  const projects = data || [];
  const active = projects.filter((p) => !["complete", "on_hold"].includes(p.status));
  const first = (user.name || "").split(" ")[0];
  const awaiting = (today.data?.awaiting || []).length;

  return (
    <div className="p-4 md:p-8" data-testid={`dashboard-${user.role}`}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3" data-testid="today-header">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground" data-testid="dashboard-season">{longDate()}{workspace?.name ? ` · ${workspace.name}` : ""}</p>
          <h1 className="page-title mt-1" data-testid="dashboard-greeting">{greeting()}{first && first.toLowerCase() !== "studio" ? `, ${first}` : ""}</h1>
        </div>
        {admin && !loading && (
          <div className="grid grid-cols-2 gap-1 rounded-xl border bg-card p-1 sm:flex" data-testid="stats-strip">
            <Stat label="Active projects" value={active.length} testid="stat-active-projects" to="/projects" />
            <Stat label="In design" value={projects.filter((p) => p.status === "design").length} testid="stat-in-design" to="/projects" />
            <Stat label="Active budget" value={money(active.reduce((s, p) => s + (p.budget || 0), 0)).replace("CA", "")} testid="stat-active-budget" to="/projects" />
            <Stat label="Awaiting client" value={awaiting} testid="stat-awaiting-client" to="/projects" />
          </div>
        )}
      </header>
      {user.role === "client" && !loading && <ClientWelcome project={projects[0]} />}
      {user.role === "crew" && <MyWeek />}
      {user.role !== "client" && <TodayList today={today} hideDecisions={digest} />}
      {digest && <div className="mt-4"><DecisionsDigest /></div>}
      <div className="mb-4 mt-8 flex items-end justify-between">
        <h2 className="section-title">{user.role === "client" ? "Your project" : admin ? "Recent projects" : "Your projects"}</h2>
        {user.role !== "client" && <Button asChild variant="ghost" size="sm" data-testid="view-all-projects-link"><Link to="/projects">View all <ArrowUpRight className="ml-1 h-4 w-4" /></Link></Button>}
      </div>
      {loading ? <Loading rows={2} /> : error ? <ErrorState message={error} onRetry={reload} /> : projects.length === 0 ? (
        <Empty icon={user.role === "client" ? Layers : FolderKanban} title="Nothing here yet"
          text={admin ? "Start by creating a project. Each one gets its own schedule, board and estimate." : "You haven't been added to a project yet. Check back soon."}
          action={admin && <Button asChild className="rounded-full" data-testid="dashboard-create-project"><Link to="/projects">Create a project</Link></Button>} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {projects.slice(0, 6).map((p, i) => <ProjectCard key={p.id} p={p} i={i} showBudget={admin} />)}
        </div>
      )}
      {user.platform_owner && <div className="mt-8"><PlatformGlance /></div>}
    </div>
  );
}
